import { queryClient } from '../store/provider/TanStackProvider'

const CLINIC_UPDATE_DEBOUNCE_MS = 150

/**
 * Rails model names (and a few aliases) mapped to the React Query key prefixes
 * already used by the data hooks. Explicit `query_keys` on an event skip this map.
 */
const RESOURCE_ALIASES: Record<string, readonly string[]> = {
  admission: ['admissions', 'admission_orders', 'admission_summary'],
  admission_order: ['admission_orders', 'encounters', 'bedRequest'],
  admission_summary: ['admission_summary'],
  allergies: ['allergies'],
  allergy: ['allergies'],
  appointment: ['appointments', 'appointment', 'patient_dashboard'],
  appointments: ['appointments', 'appointment', 'patient_dashboard'],
  archive: ['archives', 'archive-doc', 'archive-doc-audit-report'],
  archive_doc: ['archive-doc', 'archive-doc-audit-report', 'archives'],
  audit: ['audits'],
  bed: ['beds'],
  bed_request: ['bedRequest', 'beds'],
  beds: ['beds'],
  billing: ['invoice', 'invoices'],
  charge_item: ['chargeItems'],
  clinic_summary: ['clinic_summary'],
  code: ['codes'],
  config: ['config'],
  consent_form: ['consent_forms', 'intake_consent', 'intake', 'template_doc'],
  dashboard: ['dashboard'],
  department: ['department'],
  dispensing: ['dispensing', 'medication', 'product_inventories', 'products'],
  drug: ['drugs', 'drug_search'],
  encounter: ['encounters', 'dashboard', 'intake'],
  encounters: ['encounters', 'dashboard', 'intake'],
  facility: ['facilities'],
  family: ['family'],
  hmo: ['hmos'],
  hmo_plan: ['hmo_plans', 'hmos'],
  hmo_tariff: ['hmoTarrifs', 'hmoTarrif', 'hmos', 'hmo_plans'],
  hmo_tarrif: ['hmoTarrifs', 'hmoTarrif', 'hmos', 'hmo_plans'],
  imaging: ['imagings'],
  imaging_order: ['imaging_order', 'orders', 'patient_imaging_orders'],
  imaging_result: ['imaging_result', 'orders', 'imaging_order'],
  imaging_test_template: ['imaging_test_temp'],
  intake: ['intake', 'appointment'],
  inventory_location: ['inventory-locations'],
  invitation: ['staffs'],
  invitations: ['staffs'],
  invoice: ['invoice', 'invoices'],
  invoices: ['invoice', 'invoices'],
  lab_order: ['lab_order', 'orders', 'patient_lab_orders'],
  lab_result: ['lab_result', 'orders', 'lab_order'],
  lab_sample: ['lab_sample', 'orders'],
  lab_test: ['lab_test'],
  lab_test_template: ['lab_test_temp'],
  location: ['location'],
  medical: ['medical'],
  medication: ['medication', 'medication_administration', 'patient_medications'],
  medication_administration: ['medication_administration', 'administration', 'medication'],
  medication_order: ['medication', 'medication_administration', 'patient_medications'],
  note: ['note'],
  notes: ['note'],
  notification: ['notifications'],
  order: ['orders', 'consent_forms', 'lab_order'],
  orders: ['orders', 'consent_forms', 'lab_order'],
  patient: ['patients', 'invoice', 'invoices'],
  patients: ['patients', 'invoice', 'invoices'],
  pharmacy_summary: ['pharmacy_summary'],
  procedure: ['procedure'],
  product: ['products'],
  product_inventory: ['product_inventories', 'products'],
  profile: ['profile'],
  registration_submission: ['registration_submissions', 'patients'],
  requisition: ['requisitions', 'requisition'],
  role: ['roles', 'staffs'],
  room: ['room'],
  schedule: ['schedules', 'schedule'],
  shift: ['shifts'],
  social: ['social'],
  specialty: ['specialty', 'config'],
  staff: ['staffs'],
  staff_invitation: ['staffs'],
  staffs: ['staffs'],
  surgery: ['surgeries'],
  template_doc: ['template_doc', 'intake_consent'],
  unit: ['units'],
  vital_sign: ['vital_signs', 'intake', 'patient_vital_signs'],
  vital_signs: ['vital_signs', 'intake', 'patient_vital_signs']
}

export type ClinicUpdateMessage = {
  action?: string
  attributes?: { notifiable_type?: unknown }
  id?: string | number | null
  notifiable_type?: unknown
  query_keys?: unknown
  resource?: unknown
}

const pendingKeys: string[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

const normalizeResource = (value: string) =>
  value
    .trim()
    .replace(/::/g, '_')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase()

const unique = (keys: readonly string[]) => {
  const seen: Record<string, true> = {}
  const result: string[] = []
  keys.forEach(key => {
    if (seen[key]) return
    seen[key] = true
    result.push(key)
  })
  return result
}

const asKeyList = (value: unknown) => {
  if (!Array.isArray(value)) return []

  return value
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .map(item => item.trim())
}

const parseMessage = (message: unknown): ClinicUpdateMessage => {
  if (typeof message === 'string') {
    try {
      return parseMessage(JSON.parse(message))
    } catch {
      return {}
    }
  }

  if (!message || typeof message !== 'object') return {}
  return message as ClinicUpdateMessage
}

const resourceName = (payload: ClinicUpdateMessage) => {
  if (typeof payload.resource === 'string' && payload.resource.trim()) return payload.resource
  if (typeof payload.notifiable_type === 'string' && payload.notifiable_type.trim()) return payload.notifiable_type
  if (typeof payload.attributes?.notifiable_type === 'string' && payload.attributes.notifiable_type.trim()) {
    return payload.attributes.notifiable_type
  }
  return ''
}

export const resolveClinicQueryKeys = (message: unknown) => {
  const payload = parseMessage(message)
  const explicit = asKeyList(payload.query_keys)
  if (explicit.length) return unique(explicit)

  const named = resourceName(payload)
  if (!named) return []

  const resource = normalizeResource(named)
  return unique(RESOURCE_ALIASES[resource] ?? [resource])
}

const flushClinicUpdates = () => {
  const keys = pendingKeys.slice()
  pendingKeys.length = 0
  flushTimer = null

  keys.forEach(key => {
    void queryClient.invalidateQueries({ queryKey: [key] })
  })
}

const rememberKey = (key: string) => {
  if (pendingKeys.indexOf(key) === -1) pendingKeys.push(key)
}

export const applyClinicUpdate = (message: unknown) => {
  resolveClinicQueryKeys(message).forEach(rememberKey)
  if (pendingKeys.length === 0 || flushTimer) return

  flushTimer = setTimeout(flushClinicUpdates, CLINIC_UPDATE_DEBOUNCE_MS)
}

export const resetClinicUpdateQueue = () => {
  if (flushTimer) clearTimeout(flushTimer)
  flushTimer = null
  pendingKeys.length = 0
}
